import React, {useCallback, useMemo, useState, useEffect} from "react";
import {ConfigProvider, Tag, message, Spin, Collapse} from "antd";
import {StringBuilder} from "../../AppUtils.js";
import {useRequest, useGeoLocation} from "../../utils/hooks.jsx";
import {OrderActions} from "../../event/OrderActionList/OrderActions.jsx";
import {DeliveryContentConfirmation} from "./DeliveryContentConfirmation.jsx";
import "./driver-delivery-info-page.css";
import "../deliveryInfoPage/delivery-info-page.css";
import {
    DeliveryProviderDeliveryStatus,
    DeliveryProviderDeliveryStatusToText,
    DeliveryStatusToColor
} from "../../dashboards/DeliveryProviderDashboard/DeliveryProviderDeliveries/consts.jsx";
import {SystemIdToIcon} from "../../admin/AdminOrders/consts.jsx";
import {SourceDeliverySection} from "../deliveryInfoPage/SourceDeliverySection.jsx";
import {DestinationDeliverySection} from "../deliveryInfoPage/DestinationDeliverySection.jsx";
import {addDeliveryStatusMessage, changeDeliveryStatus, updateDeliveries} from "../../api/deliveries/deliveries.js";
import "../deliveryInfoPage/delivery-info-page.css";
import {calculateDeliveryTime} from "../../event/utils.jsx";
import moment from "moment";
import {
    collapseKeyToStatus,
    getActionsByCollapseKey,
    statusHistoryToText,
    statusHistoryPriorityByCollapseKey
} from "./utils.jsx";
import {DownOutlined} from "@ant-design/icons";
import {DeliverySignatureComponent} from "../../components";
import {UploadClient} from "../../http/UploadClient";
import {EventBus} from "../../bus/EventBus";
import {DeliveryNavigateModal} from "./DeliveryNavigateModal.jsx";
import {imageProps, getPanelKeyByStatus} from "./utils.jsx";
import {isEmpty} from "lodash";

export const DriverDeliveryInfoPage = ({match}) => {
    const {deliveryId} = match.params;
    const [loading, setLoading] = useState(false);
    const [activePanels, setActivePanels] = useState(["1"]);
    const [buisinessSignature, setBuisinessSignature] = useState(null);
    const [clientSignature, setClientSignature] = useState(null);
    const [showNavigateModal, setShowNavigateModal] = useState(false);
    const [deliveryLeftAtDestination, setDeliveryLeftAtDestination] = useState(false);

    const [deliveryInfo = {}, isLoadingDeliveryInfo, error, fetchDeliveryInfo] = useRequest(
        `/api/deliveryProviders/deliveries/${deliveryId}`,
        "GET",
        null,
        [deliveryId],
        !!deliveryId,
        null,
        {
            delivery: {},
            subDeliveries: [],
            order: {}
        }
    );

    const {
        position,
        loading: loadingLocation,
        locationError,
        stopTracking,
        checkGeoPermission
    } = useGeoLocation(false, 10000);

    const isDeliveryInTransit =
        deliveryInfo?.delivery?.status === DeliveryProviderDeliveryStatus.pickedUp ||
        deliveryInfo?.delivery?.status === DeliveryProviderDeliveryStatus.onTheWay;

    const shouldFetchRouteInfo =
        isDeliveryInTransit && position?.lat && position?.lng && !!deliveryInfo?.delivery?.destinationAddress?.address;

    const [routeInfo] = useRequest(
        `/api/location/updateDriverDistance`,
        "POST",
        {
            currentDriverLocation: position,
            destinationAddress: deliveryInfo?.delivery?.destinationAddress?.address,
            deliveryId: deliveryInfo?.delivery?.deliveryId
        },
        [position, isDeliveryInTransit],
        shouldFetchRouteInfo
    );

    const statusBasedPanelKey = useMemo(
        () => getPanelKeyByStatus(deliveryInfo?.delivery?.status),
        [deliveryInfo?.delivery?.status]
    );

    useEffect(() => {
        setActivePanels(prev => {
            if (prev.length === 1 && prev[0] === statusBasedPanelKey) {
                return prev;
            }
            return [statusBasedPanelKey];
        });
    }, [statusBasedPanelKey]);

    const onBuisinessSignatureChange = useCallback(async signature => {
        if (!signature) {
            setBuisinessSignature(null);
            return;
        }
        try {
            setLoading(true);
            const formData = new FormData();
            formData.append("file", signature);
            const {imageUrl, imageId} = await UploadClient.upload(
                "PUT",
                `/api/image?width=${imageProps.width}&height=${imageProps.height}&crop=fill&gravity=center&colors=1`,
                formData
            );
            setBuisinessSignature({signatureUrl: imageUrl, signatureId: imageId});
            setLoading(false);
        } catch (e) {
            setLoading(false);
            EventBus.triggerError(
                "server-error",
                {content: {description: "Unfortunately we couldn't upload your signature :("}},
                e.message
            );
        }
    }, []);

    const onClientSignatureChange = useCallback(async (signature, leftAtDestination = false) => {
        if (!signature) {
            setClientSignature(null);
            return;
        } else {
            setDeliveryLeftAtDestination(leftAtDestination);
        }
        try {
            setLoading(true);
            const formData = new FormData();
            formData.append("file", signature);
            const {imageUrl, imageId} = await UploadClient.upload(
                "PUT",
                `/api/image?width=${imageProps.width}&height=${imageProps.height}&crop=fill&gravity=center&colors=1`,
                formData
            );
            setClientSignature({signatureUrl: imageUrl, signatureId: imageId});
            setLoading(false);
        } catch (e) {
            setLoading(false);
            EventBus.triggerError(
                "server-error",
                {content: {description: "Unfortunately we couldn't upload your signature :("}},
                e.message
            );
        }
    }, []);

    const onDeliveryStatusChange = useCallback(
        async status => {
            setLoading(true);
            try {
                if (
                    status === DeliveryProviderDeliveryStatus.onTheWay ||
                    status === DeliveryProviderDeliveryStatus.pickedUp
                ) {
                    const granted = await checkGeoPermission();
                    if (!granted) {
                        navigator.geolocation.getCurrentPosition(
                            p => console.log("OK", p),
                            e => console.error("ERR", e)
                        );
                        message.error("יש לאפשר גישה למיקום ולנסות שוב");
                        return false;
                    }
                }
                await changeDeliveryStatus(deliveryInfo?.delivery?.deliveryId, status);
                fetchDeliveryInfo();
                return true;
            } catch (error) {
                return false;
            } finally {
                setLoading(false);
            }
        },
        [deliveryInfo?.delivery?.deliveryId, fetchDeliveryInfo, checkGeoPermission]
    );

    const onStartNavigation = useCallback(() => {
        setShowNavigateModal(true);
        onDeliveryStatusChange(DeliveryProviderDeliveryStatus.onTheWay);
    }, [onDeliveryStatusChange]);

    const renderPanelHeader = useCallback(
        (title, key, rightContent = null) => (
            <div className="delivery-info-collapse-header">
                <div>
                    <span>{title}</span>
                    <DownOutlined
                        style={{
                            transform: activePanels.includes(key) ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.3s",
                            fontSize: "12px",
                            marginRight: "10px"
                        }}
                    />
                </div>
                {rightContent}
            </div>
        ),
        [activePanels]
    );

    const renderStatusHistoryEntry = useCallback(
        statusKey => {
            const history = deliveryInfo?.delivery?.statusHistory;
            if (!history?.length) {
                return null;
            }

            const historyStatus = statusHistoryPriorityByCollapseKey[statusKey];

            const entry = historyStatus ? history.findLast(item => historyStatus.includes(item.status)) : null;

            if (!entry) {
                return null;
            }

            return (
                <div className="delivery-status-message-container">
                    <span className="delivery-status-message-title">{statusHistoryToText[entry.status]} :</span>
                    <span className="delivery-status-message-description">
                        {moment(entry.createdAt).format("HH:mm")}
                    </span>
                </div>
            );
        },
        [deliveryInfo?.delivery?.statusHistory]
    );

    const onUpdateDeliveries = useCallback(
        async status => {
            if (status === DeliveryProviderDeliveryStatus.pickedUp) {
                await updateDeliveries([
                    {
                        deliveryId: deliveryInfo?.delivery?.deliveryId,
                        businessSignature: buisinessSignature
                    }
                ]);
            } else if (status === DeliveryProviderDeliveryStatus.supplied) {
                await updateDeliveries([
                    {
                        deliveryId: deliveryInfo?.delivery?.deliveryId,
                        clientSignature: clientSignature,
                        deliveryLeftAtDestination: deliveryLeftAtDestination
                    }
                ]);
            }
            await onDeliveryStatusChange(status);
        },
        [
            buisinessSignature,
            clientSignature,
            deliveryInfo?.delivery?.deliveryId,
            deliveryLeftAtDestination,
            onDeliveryStatusChange
        ]
    );

    const onUpdateDeliveryHistory = useCallback(
        async (key, label) => {
            await addDeliveryStatusMessage(deliveryInfo?.delivery?.deliveryId, label);
            fetchDeliveryInfo();
        },
        [deliveryInfo?.delivery?.deliveryId, fetchDeliveryInfo]
    );

    return (
        <div className="delivery-info-page">
            {isEmpty(deliveryInfo?.delivery) ? (
                <Spin />
            ) : (
                <ConfigProvider direction="rtl">
                    <div className="header-container">
                        <span className="title">
                            {new StringBuilder("הזמנה").append(deliveryInfo?.delivery?.proposalId).toString()}
                        </span>
                        <Tag
                            className="status-tag"
                            style={{
                                color: DeliveryStatusToColor?.[deliveryInfo?.delivery?.status]?.[0],
                                backgroundColor: DeliveryStatusToColor?.[deliveryInfo?.delivery?.status]?.[1]
                            }}>
                            {DeliveryProviderDeliveryStatusToText[deliveryInfo?.delivery?.status]}
                        </Tag>
                        <img
                            className="system-id-icon"
                            src={SystemIdToIcon[deliveryInfo?.order.systemId]}
                            alt="systemId_icon"
                        />
                    </div>
                    <div className="delivery-info-container">
                        <Collapse
                            defaultActiveKey={[statusBasedPanelKey]}
                            activeKey={activePanels}
                            onChange={setActivePanels}
                            ghost
                            expandIcon={() => null}
                            className="delivery-info-collapse">
                            <Collapse.Panel
                                header={renderPanelHeader(
                                    "איסוף",
                                    "1",
                                    <span className="delivery-time-label">
                                        {calculateDeliveryTime(deliveryInfo?.order ?? {})?.format("HH:mm")}
                                    </span>
                                )}
                                key="1"
                                collapsible={
                                    !collapseKeyToStatus[1].includes(deliveryInfo?.delivery?.status)
                                        ? "disabled"
                                        : "open"
                                }
                                className="order-status-view-container"
                                style={activePanels.includes("1") ? {borderColor: "#F7B348"} : {}}>
                                <SourceDeliverySection
                                    deliveryInfo={deliveryInfo}
                                    onDeliveryStatusChange={onDeliveryStatusChange}
                                    showHeader={false}
                                />
                                {renderStatusHistoryEntry("1")}
                                {!isLoadingDeliveryInfo ? (
                                    <OrderActions
                                        className="delivery-action-buttons"
                                        actions={getActionsByCollapseKey(
                                            "1",
                                            () =>
                                                onDeliveryStatusChange(
                                                    DeliveryProviderDeliveryStatus.arrivedAtBusiness
                                                ),
                                            loading,
                                            {
                                                status: deliveryInfo?.delivery?.status,
                                                onMenuAction: onUpdateDeliveryHistory
                                            }
                                        )}
                                    />
                                ) : (
                                    <Spin style={{display: "block"}} />
                                )}
                            </Collapse.Panel>
                            <Collapse.Panel
                                header={renderPanelHeader("אישור פרטי תוכן הזמנה - ספק", "2")}
                                key="2"
                                collapsible={
                                    !collapseKeyToStatus[2].includes(deliveryInfo?.delivery?.status)
                                        ? "disabled"
                                        : "open"
                                }
                                className="order-status-view-container"
                                style={activePanels.includes("2") ? {borderColor: "#ED7FA6"} : {}}>
                                <DeliveryContentConfirmation eventInfo={deliveryInfo?.order || {}} />
                                <DeliverySignatureComponent onChange={onBuisinessSignatureChange} />
                                {renderStatusHistoryEntry("2")}
                                {!isLoadingDeliveryInfo ? (
                                    <OrderActions
                                        className="delivery-action-buttons"
                                        actions={getActionsByCollapseKey(
                                            "2",
                                            async () =>
                                                await onUpdateDeliveries(DeliveryProviderDeliveryStatus.pickedUp),
                                            loading,
                                            {
                                                status: deliveryInfo?.delivery?.status,
                                                disabled: !buisinessSignature,
                                                onMenuAction: onUpdateDeliveryHistory
                                            }
                                        )}
                                    />
                                ) : (
                                    <Spin style={{display: "block"}} />
                                )}
                            </Collapse.Panel>
                            <Collapse.Panel
                                header={renderPanelHeader("נווט אל הלקוח", "3")}
                                key="3"
                                collapsible={
                                    !collapseKeyToStatus[3].includes(deliveryInfo?.delivery?.status)
                                        ? "disabled"
                                        : "open"
                                }
                                className="order-status-view-container"
                                style={activePanels.includes("3") ? {borderColor: "#1260D1"} : {}}>
                                <DestinationDeliverySection
                                    deliveryInfo={deliveryInfo}
                                    onDeliveryStatusChange={onDeliveryStatusChange}
                                    showHeader={false}
                                    openAddressInNewTab={false}
                                />
                                <span className="delivery-info-collapse-description">
                                    לחיצה על הכפתור תפעיל את החיוב ותעדכן את הלקוח שהמשלוח יוצא לדרך
                                </span>
                                {renderStatusHistoryEntry("3")}
                                {!isLoadingDeliveryInfo ? (
                                    <OrderActions
                                        className="delivery-action-buttons"
                                        actions={getActionsByCollapseKey("3", () => onStartNavigation(), loading, {
                                            status: deliveryInfo?.delivery?.status,
                                            onMenuAction: onUpdateDeliveryHistory
                                        })}
                                    />
                                ) : (
                                    <Spin style={{display: "block"}} />
                                )}
                            </Collapse.Panel>
                            <Collapse.Panel
                                header={renderPanelHeader(
                                    "מסירה",
                                    "4",
                                    !!deliveryInfo?.order?.until ? (
                                        <div className="delivery-time-label">
                                            <span>{moment(deliveryInfo?.order?.until)?.format("HH:mm")}</span>
                                            <span>-</span>
                                            <span>{moment(deliveryInfo?.order?.dtstart)?.format("HH:mm")}</span>
                                        </div>
                                    ) : (
                                        <span className="delivery-time-label">
                                            {moment(deliveryInfo?.order?.dtstart)?.format("HH:mm")}
                                        </span>
                                    )
                                )}
                                key="4"
                                collapsible={
                                    !collapseKeyToStatus[4].includes(deliveryInfo?.delivery?.status)
                                        ? "disabled"
                                        : "open"
                                }
                                className="order-status-view-container"
                                style={activePanels.includes("4") ? {borderColor: "#00C693"} : {}}>
                                <DestinationDeliverySection
                                    deliveryInfo={deliveryInfo}
                                    onDeliveryStatusChange={onDeliveryStatusChange}
                                    showHeader={false}
                                />
                                {renderStatusHistoryEntry("4")}
                                {!isLoadingDeliveryInfo ? (
                                    <OrderActions
                                        className="delivery-action-buttons"
                                        actions={getActionsByCollapseKey(
                                            "4",
                                            () =>
                                                onDeliveryStatusChange(
                                                    DeliveryProviderDeliveryStatus.arrivedAtDestination
                                                ),
                                            loading,
                                            {
                                                status: deliveryInfo?.delivery?.status,
                                                onMenuAction: onUpdateDeliveryHistory
                                            }
                                        )}
                                    />
                                ) : (
                                    <Spin style={{display: "block"}} />
                                )}
                            </Collapse.Panel>
                            <Collapse.Panel
                                header={renderPanelHeader("אישור פרטי תוכן הזמנה - לקוח", "5")}
                                key="5"
                                collapsible={
                                    !collapseKeyToStatus[5].includes(deliveryInfo?.delivery?.status)
                                        ? "disabled"
                                        : "open"
                                }
                                className="order-status-view-container"
                                style={activePanels.includes("5") ? {borderColor: "#00C693"} : {}}>
                                <DeliveryContentConfirmation eventInfo={deliveryInfo?.order || {}} />
                                <DeliverySignatureComponent
                                    title="חתימת לקוח"
                                    onChange={onClientSignatureChange}
                                    allowImageUpload
                                />
                                {renderStatusHistoryEntry("5")}
                                {!isLoadingDeliveryInfo ? (
                                    <OrderActions
                                        className="delivery-action-buttons"
                                        actions={getActionsByCollapseKey(
                                            "5",
                                            async () =>
                                                await onUpdateDeliveries(DeliveryProviderDeliveryStatus.supplied),
                                            loading,
                                            {
                                                status: deliveryInfo?.delivery?.status,
                                                disabled: !clientSignature,
                                                onMenuAction: onUpdateDeliveryHistory
                                            }
                                        )}
                                    />
                                ) : (
                                    <Spin style={{display: "block"}} />
                                )}
                            </Collapse.Panel>
                        </Collapse>
                    </div>
                    {!!(
                        deliveryInfo?.delivery?.status === DeliveryProviderDeliveryStatus.failed &&
                        !!deliveryInfo?.delivery?.statusMessage
                    ) && (
                        <div style={{display: "flex", flexDirection: "column"}}>
                            <span className="title" style={{color: "red"}}>
                                תיאור התקלה
                            </span>
                            <span>{deliveryInfo?.delivery?.statusMessage}</span>
                        </div>
                    )}
                </ConfigProvider>
            )}
            {showNavigateModal && (
                <DeliveryNavigateModal
                    open={showNavigateModal}
                    location={deliveryInfo?.delivery?.destinationAddress}
                    onClose={() => setShowNavigateModal(false)}
                />
            )}
        </div>
    );
};
